import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { ExerciseItem } from "../../components/exercise/ExerciseItem";
import { Add } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import { AddExerciseDialog } from "../../components/exercise/AddExerciseDialog";
import { useSnackbar } from "notistack";

const ExercisePage = () => {
  const { token } = useAuthContext();
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [exercises, setExercises] = useState([]); // State to store exercises fetched from backend
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [filteredExercises, setFilteredExercises] = useState([]); // State for filtered exercises
  const { darkMode } = useContext(ThemeContext);
  const [openAddDialog, setOpenAddDialog] = useState(false); // New state for confirmation dialog
  const { enqueueSnackbar } = useSnackbar();

  const updateExercise = (updatedExercise) => {
    setExercises((prevExercises) => {
      let updatedExercises;

      if (updatedExercise.deleted) {
        // Remove the deleted exercise from the state
        updatedExercises = prevExercises.filter(
          (exercise) => exercise.id !== updatedExercise.id
        );
      } else if (
        !prevExercises.some((exercise) => exercise.id === updatedExercise.id)
      ) {
        // Add the new exercise if it doesn't already exist
        updatedExercises = [...prevExercises, updatedExercise];
      } else {
        // Update the existing exercise
        updatedExercises = prevExercises.map((exercise) =>
          exercise.id === updatedExercise.id ? updatedExercise : exercise
        );
      }

      // Sort exercises alphabetically by name
      return updatedExercises.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );
    });
  };

  useEffect(() => {
    // Fetch exercises from the backend
    if (!token) return;
    const fetchExercises = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/exercise`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setExercises(response.data);
        setFilteredExercises(response.data); // Initialize filtered exercises
      } catch (err) {
        console.error("Error fetching exercises:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchExercises();
  }, [token]);

  useEffect(() => {
    // Filter exercises based on the search query
    setFilteredExercises(
      exercises.filter((exercise) =>
        exercise.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, exercises]);

  const handleAddExercise = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/exercise`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      updateExercise(response.data); // Update the parent state
      enqueueSnackbar("Successfully Added!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to add exercise!", { variant: "error" });
    }
  };

  const handleAddClicked = () => {
    setOpenAddDialog(true);
  };

  const handleCancelAdd = () => {
    setOpenAddDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          pt: "60px",
          pl: 5,
          pr: 10,
        }}
      >
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
        {!loading && (
          <>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "28px",
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                fontWeight: 600,
                textAlign: "left",
                mb: 5,
              }}
            >
              Base Exercise Management
            </Typography>
            <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
              <TextField
                variant="filled"
                placeholder="Search exercises"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <SearchIcon sx={{ mt: -2, ml: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                startIcon={<Add />}
                variant="contained"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 500,
                  px: 3,
                  py: 2,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleAddClicked}
              >
                Add Exercise
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={4}
              rowGap={4}
            >
              {filteredExercises.map((exercise, index) => (
                <ExerciseItem
                  key={index}
                  {...exercise}
                  onExerciseUpdate={updateExercise}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
      <AddExerciseDialog
        open={openAddDialog}
        onClose={handleCancelAdd}
        onAdd={handleAddExercise}
      />
    </>
  );
};

export default ExercisePage;
